import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Event,
  EventEmitter
} from "@stencil/core";

import { Size } from "@genexus/gemini/dist/types/components/icon/icon";
import { CheckedItemsInfo } from "../list-selector/list-selector";
import { Locale } from "../../../common/locale";
@Component({
  tag: "gx-ide-status-buttons",
  styleUrl: "gx-ide-status-buttons.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/status-buttons"]
})
export class GxIdeStatusButtons {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  /**
   * Whether or not the buttons are active
   */
  @Prop() readonly active: boolean = false;

  /**
   * The errors amount
   */
  @Prop() readonly errors: number = 0;

  /**
   * Hide the errors button
   */
  @Prop() readonly hideError: boolean = false;

  /**
   * The warnings amount
   */
  @Prop() readonly warnings: number = 0;

  /**
   * Hide the warnings button
   */
  @Prop() readonly hideWarning: boolean = false;

  /**
   * The successes amount
   */
  @Prop() readonly successes: number = 0;

  /**
   * Hide the successes button
   */
  @Prop() readonly hideSuccess: boolean = false;

  /**
   * The messages amount
   */
  @Prop() readonly messages: number = 0;

  /**
   * Hide the messages button
   */
  @Prop() readonly hideMessage: boolean = false;

  /**
   * Display only the number
   */
  @Prop() readonly minimal: boolean = false;

  /**
   * If present will make the component look smaller.
   */
  @Prop({ reflect: true }) readonly compact: boolean = false;

  /**
   * No inner borders
   */
  @Prop({ reflect: true }) readonly noInnerBorders: boolean = false;

  @Element() el: HTMLGxIdeStatusButtonsElement;

  @State() iconsSize: Size = "small";

  /**
   * Emits the actual selection
   */
  @Event() selectionChanged: EventEmitter<CheckedItemsInfo>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.evaluateIconsSize();
  }

  private evaluateIconsSize = () => {
    if (this.compact) {
      this.iconsSize = "small";
    }
  };

  private evaluateLabel = (
    type: "errors" | "warnings" | "successes" | "messages"
  ): string => {
    const amount = this[type];
    if (this.minimal) {
      return `${amount}`;
    } else {
      const singular: boolean = amount === 1;
      return singular
        ? `${amount} ${this.#componentLocale[type]["singular"]}`
        : `${amount} ${this.#componentLocale[type]["plural"]}`;
    }
  };

  private selectionChangedHandler = (e: CustomEvent<CheckedItemsInfo>) => {
    e.stopPropagation();
    this.selectionChanged.emit(e.detail);
  };

  render() {
    return (
      <Host>
        <gx-ide-list-selector
          type="multi-selection"
          listName="status-buttons"
          noListBorder
          ellipsis
          onSelectionChanged={this.selectionChangedHandler}
        >
          {!this.hideError ? (
            <gx-ide-list-selector-item
              itemId="errors"
              itemValue={this.evaluateLabel("errors")}
              icon="gx-test/result-exception"
              class={{ "error": true, "not-zero": this.errors !== 0 }}
              itemChecked
            >
              <span
                slot="universal"
                class={{
                  "pseudo-checkbox": true
                }}
              ></span>
            </gx-ide-list-selector-item>
          ) : null}
          {!this.hideWarning ? (
            <gx-ide-list-selector-item
              itemId="warnings"
              itemValue={this.evaluateLabel("warnings")}
              icon="gx-test/result-warning"
              class={{ "warning": true, "not-zero": this.warnings !== 0 }}
              itemChecked
            >
              <span
                slot="universal"
                class={{
                  "pseudo-checkbox": true
                }}
              ></span>
            </gx-ide-list-selector-item>
          ) : null}
          {!this.hideSuccess ? (
            <gx-ide-list-selector-item
              itemId="successes"
              itemValue={this.evaluateLabel("successes")}
              icon="gx-test/result-ok"
              class={{ "success": true, "not-zero": this.successes !== 0 }}
              itemChecked
            >
              <span
                slot="universal"
                class={{
                  "pseudo-checkbox": true
                }}
              ></span>
            </gx-ide-list-selector-item>
          ) : null}
          {!this.hideMessage ? (
            <gx-ide-list-selector-item
              itemId="messages"
              itemValue={this.evaluateLabel("messages")}
              icon="gx-test/screenshot-viewer"
              class={{ "message": true, "not-zero": this.messages !== 0 }}
              itemChecked
            >
              <span
                slot="universal"
                class={{
                  "pseudo-checkbox": true
                }}
              ></span>
            </gx-ide-list-selector-item>
          ) : null}
        </gx-ide-list-selector>
      </Host>
    );
  }
}
